class CafeNow extends HTMLElement {
  connectedCallback() {
    this._timezone = this.getAttribute('data-timezone') || 'Europe/Brussels';
    this._locale = this.getAttribute('data-locale') || 'nl';
    this._status = this.querySelector('[data-cafe-status]');
    this._statusText = this.querySelector('[data-cafe-status-text]');

    try {
      this._hours = JSON.parse(this.getAttribute('data-hours') || '[]').map((day) => this._parseDay(day));
    } catch {
      this._hours = [];
    }

    this._render();

    this._interval = setInterval(() => this._render(), 60_000);
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  _parseDay(value) {
    if (typeof value !== 'string') return [];

    return value
      .split(',')
      .map((range) => {
        const match = range.trim().match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
        if (!match) return null;

        const from = Number(match[1]) * 60 + Number(match[2]);
        const to = Number(match[3]) * 60 + Number(match[4]);

        return to > from ? [from, to] : null;
      })
      .filter(Boolean);
  }

  _now() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this._timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const value = (type) => parts.find((part) => part.type === type)?.value || '';
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return {
      day: Math.max(0, days.indexOf(value('weekday'))),
      minutes: Number(value('hour')) * 60 + Number(value('minute')),
    };
  }

  _formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }

  _dayName(offset) {
    const date = new Date();
    date.setDate(date.getDate() + offset);

    return new Intl.DateTimeFormat(this._locale, { timeZone: this._timezone, weekday: 'long' }).format(date);
  }

  _nextOpening(now) {
    for (let offset = 0; offset < 7; offset++) {
      const ranges = this._hours[(now.day + offset) % 7] || [];

      for (const [from] of ranges) {
        if (offset > 0 || from > now.minutes) {
          return { offset, from };
        }
      }
    }

    return null;
  }

  _render() {
    if (!this._status || !this._statusText || this._hours.length === 0) return;

    const now = this._now();
    const today = this._hours[now.day] || [];
    const openRange = today.find(([from, to]) => now.minutes >= from && now.minutes < to);

    let text = '';
    let isOpen = false;

    if (openRange) {
      isOpen = true;
      const minutesLeft = openRange[1] - now.minutes;
      const template =
        minutesLeft <= 60 && this.getAttribute('data-closing-soon-text')
          ? this.getAttribute('data-closing-soon-text')
          : this.getAttribute('data-open-text');

      text = this._fill(template, { time: this._formatTime(openRange[1]) });
    } else {
      const next = this._nextOpening(now);

      if (!next) {
        text = this.getAttribute('data-closed-text') || '';
      } else if (next.offset === 0) {
        text = this._fill(this.getAttribute('data-opens-later-text'), { time: this._formatTime(next.from) });
      } else {
        text = this._fill(this.getAttribute('data-opens-next-text'), {
          time: this._formatTime(next.from),
          day: this._dayName(next.offset),
        });
      }
    }

    if (text === '') return;

    this._statusText.textContent = text;
    this._status.hidden = false;
    this._status.classList.toggle('is-open', isOpen);
  }

  _fill(template, values) {
    if (!template) return '';

    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`[${key}]`, value),
      template
    );
  }
}

if (!window.customElements.get('cafe-now')) {
  window.customElements.define('cafe-now', CafeNow);
}
